import { Component } from '@theme/component';
import { debounce, onDocumentLoaded, setHeaderMenuStyle } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  #submenuMutationObserver = null;

  /**
   * Delay before dropdown closes.
   * Increase/decrease this value to adjust hover forgiveness.
   */
  #closeDelay = 300;

  /**
   * Stores pending close timer.
   */
  #closeTimeout = null;

connectedCallback() {
  super.connectedCallback();

  onDocumentLoaded(this.#preloadImages);

  window.addEventListener(
    'resize',
    this.#resizeListener
  );

  this.addEventListener(
    'pointerleave',
    this.#headerPointerLeave
  );

  this.addEventListener(
    'pointerenter',
    this.#headerPointerEnter
  );

  this.overflowMenu?.addEventListener(
    'pointerleave',
    this.#overflowSubmenuListener
  );
}

  disconnectedCallback() {
    super.disconnectedCallback();

    window.removeEventListener(
      'resize',
      this.#resizeListener
    );

    this.removeEventListener(
  'pointerleave',
  this.#headerPointerLeave
);

this.removeEventListener(
  'pointerenter',
  this.#headerPointerEnter
);

    this.overflowMenu?.removeEventListener(
      'pointerleave',
      this.#overflowSubmenuListener
    );

    if (this.#closeTimeout) {
      clearTimeout(this.#closeTimeout);
      this.#closeTimeout = null;
    }

    this.#cleanupMutationObserver();
  }

  /**
   * Debounced resize event listener.
   */
  #resizeListener = debounce(() => {
    setHeaderMenuStyle();
  }, 100);

  /**
   * Overflow menu leaves:
   * use delayed close instead of immediate close.
   */
  #overflowSubmenuListener = () => {
    this.#scheduleDeactivate();
  };

  #headerPointerLeave = () => {
  this.#scheduleDeactivate();
};

/**
 * Cancel a pending close when the pointer
 * returns to the header-menu component.
 */
#headerPointerEnter = () => {
  this.#cancelDeactivate();
};

  #state = {
    activeItem: null,
  };

  /**
   * Get overflow menu.
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (
      this.refs.overflowMenu?.shadowRoot?.querySelector(
        '[part="overflow"]'
      )
    );
  }

  /**
   * Whether overflow list is hovered.
   */
  get overflowListHovered() {
    return (
      this.refs.overflowMenu
        ?.shadowRoot
        ?.querySelector('[part="overflow-list"]')
        ?.matches(':hover') ?? false
    );
  }

  get headerComponent() {
    return /** @type {HTMLElement | null} */ (
      this.closest('header-component')
    );
  }

  /**
   * Cancel any scheduled close.
   */
  #cancelDeactivate = () => {
    if (!this.#closeTimeout) return;

    clearTimeout(this.#closeTimeout);

    this.#closeTimeout = null;
  };

  /**
   * Keep menu open when entering the submenu.
   */
  #handleSubmenuPointerEnter = () => {
    this.#cancelDeactivate();
  };

  /**
   * Schedule close when leaving the submenu.
   */
  #handleSubmenuPointerLeave = () => {
    this.#scheduleDeactivate();
  };

  /**
   * Schedule dropdown close.
   */
  #scheduleDeactivate = (item = this.#state.activeItem) => {
    this.#cancelDeactivate();

    this.#closeTimeout = setTimeout(() => {
      this.#closeTimeout = null;

      this.#deactivate(item);
    }, this.#closeDelay);
  };

  /**
   * Activate menu item.
   */
  activate = (event) => {
    /**
     * If user enters another menu item before
     * the close timer finishes, keep menu open.
     */
    this.#cancelDeactivate();

    this.dispatchEvent(
      new MegaMenuHoverEvent()
    );

    if (
      !(event.target instanceof Element) ||
      !this.headerComponent
    ) {
      return;
    }

    const item = findMenuItem(event.target);

    if (
      !item ||
      item == this.#state.activeItem
    ) {
      return;
    }

    const isDefaultSlot =
      event.target.slot === '';

    this.dataset.overflowExpanded =
      (!isDefaultSlot).toString();

    const previouslyActiveItem =
      this.#state.activeItem;

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';
    }

    this.#state.activeItem = item;

    this.ariaExpanded = 'true';

    item.ariaExpanded = 'true';

    let submenu = findSubmenu(item);

    const hasSubmenu =
      Boolean(submenu);



    if (
      !hasSubmenu &&
      !isDefaultSlot
    ) {
      submenu = this.overflowMenu;
    }

    if (submenu) {
      submenu.dataset.active = '';


      submenu.removeEventListener(
        'pointerenter',
        this.#handleSubmenuPointerEnter
      );

      submenu.removeEventListener(
        'pointerleave',
        this.#handleSubmenuPointerLeave
      );

      submenu.addEventListener(
        'pointerenter',
        this.#handleSubmenuPointerEnter
      );

      submenu.addEventListener(
        'pointerleave',
        this.#handleSubmenuPointerLeave
      );

      this.#cleanupMutationObserver();

      this.#submenuMutationObserver =
        new MutationObserver(() => {
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              if (
                submenu.offsetHeight > 0
              ) {
                this.headerComponent
                  ?.style
                  .setProperty(
                    '--submenu-height',
                    `${submenu.offsetHeight}px`
                  );

                this.#cleanupMutationObserver();
              }
            });
          });
        });

      this.#submenuMutationObserver.observe(
        submenu,
        {
          childList: true,
          subtree: true,
        }
      );

      setTimeout(() => {
        this.#cleanupMutationObserver();
      }, 500);
    }

    let finalHeight =
      submenu?.offsetHeight || 0;

    if (!isDefaultSlot) {
      const overflowListHeight =
        this.#getOverflowListLinksHeight();

      if (hasSubmenu) {
        const overflowHeight =
          this.overflowMenu?.offsetHeight || 0;

        finalHeight =
          Math.max(
            overflowHeight,
            overflowListHeight
          );
      } else {
        finalHeight =
          overflowListHeight;
      }
    }

    if (!submenu) {
      finalHeight = 0;
    }

    this.headerComponent.style.setProperty(
      '--submenu-height',
      `${finalHeight}px`
    );

    this.#setFullOpenHeaderHeight(
      finalHeight
    );

    this.style.setProperty(
      '--submenu-opacity',
      '1'
    );
  };

  /**
   * Deactivate menu item.
   *
   * Instead of immediately closing,
   * schedule the close.
   */
  deactivate(event) {
    if (
      !(event.target instanceof Element)
    ) {
      return;
    }

    const menu =
      findSubmenu(
        this.#state.activeItem
      );

    const isMovingWithinMenu =
      event.relatedTarget instanceof Node &&
      menu?.contains(
        document.activeElement
      );

    const isMovingToSubmenu =
      event.relatedTarget instanceof Node &&
      event.type === 'blur' &&
      menu?.contains(
        event.relatedTarget
      );

    const isMovingToOverflowMenu =
      event.relatedTarget instanceof Node &&
      event.relatedTarget
        .parentElement
        ?.matches('[slot="overflow"]');

    if (
      isMovingWithinMenu ||
      isMovingToOverflowMenu ||
      isMovingToSubmenu
    ) {
      return;
    }

    this.#scheduleDeactivate();
  }

  /**
   * Actually close menu.
   */
  #deactivate = (
    item = this.#state.activeItem
  ) => {
    if (
      !item ||
      item != this.#state.activeItem
    ) {
      return;
    }

    const submenu =
      findSubmenu(item);

    /**
     * If cursor returned to the regular
     * submenu or overflow menu, do not close.
     */
    if (
      submenu?.matches(':hover') ||
      this.overflowListHovered ||
      this.overflowMenu?.matches(':hover')
    ) {
      return;
    }

    this.headerComponent
      ?.style
      .setProperty(
        '--submenu-height',
        '0px'
      );

    this.#setFullOpenHeaderHeight(0);

    this.style.setProperty(
      '--submenu-opacity',
      '0'
    );

    this.dataset.overflowExpanded =
      'false';

    this.#state.activeItem = null;

    this.ariaExpanded = 'false';

    item.ariaExpanded = 'false';

    if (submenu) {
      delete submenu.dataset.active;
    }
  };

  #getOverflowListLinksHeight() {
    const slottedMenuLinks =
      this.overflowMenu
        ?.querySelector('slot')
        ?.assignedElements();

    if (!slottedMenuLinks) {
      return (
        this.overflowMenu?.offsetHeight ||
        0
      );
    }

    const mapSubmenus = (cb) => {
      slottedMenuLinks.forEach(
        (link) => {
          const submenu =
            /** @type {HTMLElement | null} */ (
              link.querySelector(
                '[ref="submenu[]"]'
              )
            );

          if (submenu) {
            cb(submenu);
          }
        }
      );
    };

    mapSubmenus(
      (submenu) => {
        submenu.style.setProperty(
          'display',
          'none'
        );
      }
    );

    const height =
      this.overflowMenu?.offsetHeight ||
      0;

    mapSubmenus(
      (submenu) => {
        submenu.style.removeProperty(
          'display'
        );
      }
    );

    return height;
  }

  #setFullOpenHeaderHeight(
    submenuHeight
  ) {
    if (!this.headerComponent) {
      return;
    }

    const isOverlapSituation =
      this.headerComponent.hasAttribute(
        'data-submenu-overlap-bottom-row'
      );

    const headerVisibleHeight =
      isOverlapSituation &&
      this.headerComponent.offsetHeight > 0
        ? /** @type {HTMLElement | null} */ (
            this.headerComponent.querySelector(
              '.header__row--top'
            )
          )?.offsetHeight ?? 0
        : this.headerComponent.offsetHeight;

    const nothingToOpen =
      submenuHeight === 0;

    const fullOpenHeaderHeight =
      nothingToOpen
        ? 0
        : submenuHeight +
          (headerVisibleHeight ?? 0);

    this.headerComponent
      ?.style
      .setProperty(
        '--full-open-header-height',
        `${fullOpenHeaderHeight}px`
      );
  }

  /**
   * Preload lazy images.
   */
  #preloadImages = () => {
    const images =
      this.querySelectorAll(
        'img[loading="lazy"]'
      );

    images?.forEach(
      (image) =>
        image.removeAttribute(
          'loading'
        )
    );
  };

  #cleanupMutationObserver() {
    this.#submenuMutationObserver
      ?.disconnect();

    this.#submenuMutationObserver =
      null;
  }
}

if (!customElements.get('header-menu')) {
  customElements.define(
    'header-menu',
    HeaderMenu
  );
}

/**
 * Find closest menu item.
 */
function findMenuItem(element) {
  if (
    !(element instanceof Element)
  ) {
    return null;
  }

  if (
    element.matches('[slot="more"]')
  ) {
    return findMenuItem(
      element.parentElement
        ?.querySelector(
          '[slot="overflow"]'
        )
    );
  }

  return element.querySelector(
    '[ref="menuitem"]'
  );
}

/**
 * Find submenu.
 */
function findSubmenu(element) {
  const submenu =
    element
      ?.parentElement
      ?.querySelector(
        '[ref="submenu[]"]'
      );

  return submenu instanceof HTMLElement
    ? submenu
    : null;
}